/**
 * Phase 1 — Rest-P2P TÜRMOB müşteri sorgusu web servisi.
 *
 * Akış:
 *   Web (bu servis)
 *     -> POST /api/PavoPayment/customer-query (gts_pavo_api)
 *     -> SignalR: Pavo cihazı `OnPavoCustomerQueryRequest`
 *     -> Pavo: `pavopay.intent.action.get.turmob.info`
 *     -> SignalR: `OnPavoCustomerQueryCompleted` / `OnPavoCustomerQueryFailed`
 *   -> UI: `PavoPayCustomerQueryResult` (klasik PavoPay ile birebir şema).
 *
 * Local PavoPay REST (`127.0.0.1:4568`) akışına dokunulmuyor —
 * `queryCustomerInfoViaRestP2P` sadece Rest-P2P aktif olduğunda çağrılır.
 */

import { pavoSignalRService } from './pavoSignalRService';
import { getRestP2PConfig } from './restP2PConfig';
import type {
  PavoPayCustomerParty,
  PavoPayCustomerQueryResult,
} from '../components/salesSystem/quickSale/hooks/fiscalDevices/pavopay';

const PAVO_API_URL =
  process.env.REACT_APP_GTS_PAVO_API_URL || 'http://localhost:5011';

const DEFAULT_TIMEOUT_MS = 30000;

function generateUuid(): string {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function ensureSignalRConnected(
  companyId?: string | null,
  branchId?: string | null,
): Promise<string | null> {
  try {
    if (!pavoSignalRService.isConnected) {
      if (companyId) {
        await pavoSignalRService.connect(companyId, branchId ?? undefined);
      } else {
        return pavoSignalRService.getConnectionId();
      }
    }
    for (let i = 0; i < 10; i++) {
      const id = pavoSignalRService.getConnectionId();
      if (id) return id;
      await new Promise((r) => setTimeout(r, 100));
    }
    return pavoSignalRService.getConnectionId();
  } catch (err) {
    console.warn('[RestP2P-Turmob] SignalR baglanti kurulamadi:', err);
    return pavoSignalRService.getConnectionId();
  }
}

interface RawTurmobInfo {
  vkn?: string;
  tckn?: string;
  ad?: string;
  soyad?: string;
  unvan?: string;
  kimlikUnvani?: string;
  vergiDairesiAdi?: string;
  vergiDairesiKodu?: string;
  faalTerkDurumu?: string;
  sirketinTuru?: string;
  iseBaslamaTarihi?: string;
  isiBirakmaTarihi?: string;
  adresBilgileri?: Array<{
    ilAdi?: string;
    ilceAdi?: string;
    mahalleSemt?: string;
    caddeSokak?: string;
    disKapiNo?: string;
    [key: string]: any;
  }>;
  [key: string]: any;
}

function extractTurmobInfo(payload: any): RawTurmobInfo | null {
  if (!payload || typeof payload !== 'object') return null;
  const direct =
    payload.TurmobInfo ||
    payload.turmobInfo ||
    payload?.result?.TurmobInfo ||
    payload?.Result?.TurmobInfo;
  if (direct && typeof direct === 'object') return direct as RawTurmobInfo;
  return null;
}

/**
 * Backend'den gelen TurmobInfo'yu web'in mevcut `PavoPayCustomerQueryResult`
 * şemasına çevirir (adapter). `CustomerQueryModal` hiç değişmeden çalışsın
 * diye `queryCustomerInfo` çıktısıyla birebir aynı formatı üretir.
 */
function adaptToCustomerQueryResult(
  info: RawTurmobInfo,
): PavoPayCustomerQueryResult {
  const hasCompanyName = !!info.unvan && info.unvan.trim() !== '';
  const hasVkn = !!info.vkn && String(info.vkn).trim() !== '';
  const isCompany = hasCompanyName && hasVkn;

  const customerParty: PavoPayCustomerParty = {
    CustomerType: isCompany ? 2 : 1,
    CompanyName: isCompany ? info.unvan || info.kimlikUnvani : undefined,
    FirstName: info.ad || '',
    FamilyName: info.soyad || '',
    TaxNumber: isCompany
      ? (info.vkn || info.tckn || '')
      : (info.tckn || info.vkn || ''),
    TaxOfficeName: info.vergiDairesiAdi || '',
    TaxOfficeCode:
      info.vkn === '9220036550' ? '001103' : (info.vergiDairesiKodu || ''),
    Country: 'Türkiye',
    City: '',
    District: '',
    Address: '',
  };

  if (info.adresBilgileri && info.adresBilgileri.length > 0) {
    const addr = info.adresBilgileri[0];
    customerParty.City = addr.ilAdi || '';
    customerParty.District = addr.ilceAdi || '';
    const parts = [
      addr.mahalleSemt,
      addr.caddeSokak,
      addr.disKapiNo ? `No:${addr.disKapiNo}` : '',
      addr.ilceAdi ? `${addr.ilceAdi}/${addr.ilAdi}` : addr.ilAdi,
    ].filter(Boolean);
    customerParty.Address = parts.join(' ');
  }

  return {
    success: true,
    customerParty,
    rawInfo: {
      vkn: info.vkn,
      tckn: info.tckn,
      unvan: info.unvan,
      kimlikUnvani: info.kimlikUnvani,
      faalTerkDurumu: info.faalTerkDurumu,
      sirketinTuru: info.sirketinTuru,
      iseBaslamaTarihi: info.iseBaslamaTarihi,
      isiBirakmaTarihi: info.isiBirakmaTarihi,
      ...info,
    },
  };
}

export interface RestP2PCustomerQueryOptions {
  timeoutMs?: number;
}

/**
 * Rest-P2P kanalı üzerinden TÜRMOB mükellef sorgusu yapar.
 * Sonuç, klasik PavoPay local servisiyle aynı `PavoPayCustomerQueryResult`
 * formatında döner; modal UI hiç değişmez.
 */
export async function queryCustomerInfoViaRestP2P(
  tckn?: string,
  vkn?: string,
  options: RestP2PCustomerQueryOptions = {},
): Promise<PavoPayCustomerQueryResult> {
  const hasTckn = !!(tckn && tckn.trim().length > 0);
  const hasVkn = !!(vkn && vkn.trim().length > 0);
  if (!hasTckn && !hasVkn) {
    return { success: false, errorMessage: 'TCKN veya VKN girin' };
  }

  const config = getRestP2PConfig();
  if (!config.enabled || !config.deviceCode) {
    return {
      success: false,
      errorMessage:
        'Rest-P2P aktif değil (cihaz ve ekran ayarları eksik). Ayarları kontrol edin.',
    };
  }

  const requestId = generateUuid();
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const callerConnectionId = await ensureSignalRConnected(
    config.companyId,
    config.branchId,
  );

  return new Promise<PavoPayCustomerQueryResult>((resolve) => {
    const subId = `restp2p-cq-${requestId}`;
    let settled = false;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      pavoSignalRService.removePavoCustomerQueryCallbacks(subId);
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
        timeoutHandle = null;
      }
    };

    const settle = (res: PavoPayCustomerQueryResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(res);
    };

    pavoSignalRService.onPavoCustomerQueryCompleted(subId, (rid, result) => {
      if (rid !== requestId) return;
      const info = extractTurmobInfo(result);
      if (!info) {
        settle({
          success: false,
          errorMessage: 'TÜRMOB sorgusundan geçerli veri alınamadı.',
        });
        return;
      }
      settle(adaptToCustomerQueryResult(info));
    });

    pavoSignalRService.onPavoCustomerQueryFailed(
      subId,
      (rid, _errorCode, message) => {
        if (rid !== requestId) return;
        settle({
          success: false,
          errorMessage: message || 'TÜRMOB sorgusu başarısız',
        });
      },
    );

    (async () => {
      try {
        const res = await fetch(
          `${PAVO_API_URL}/api/PavoPayment/customer-query`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              RequestId: requestId,
              DeviceCode: config.deviceCode,
              Tckn: hasTckn ? tckn : null,
              Vkn: hasVkn ? vkn : null,
              CompanyId: config.companyId,
              BranchId: config.branchId,
              CallerConnectionId: callerConnectionId,
            }),
          },
        );

        if (!res.ok) {
          let message = 'Rest-P2P sorgu isteği başarısız';
          if (res.status === 503) {
            message = 'Pavo cihazı çevrimdışı';
          } else {
            try {
              const body = await res.json();
              if (body?.Message) message = body.Message;
              else if (body?.message) message = body.message;
            } catch (_) {
              // json degil
            }
          }
          settle({ success: false, errorMessage: message });
          return;
        }

        timeoutHandle = setTimeout(() => {
          settle({
            success: false,
            errorMessage:
              'Cihaza ulaşılamadı (Rest-P2P TÜRMOB sorgusu zaman aşımı).',
          });
        }, timeoutMs);
      } catch (err: any) {
        console.error('[RestP2P-Turmob] POST hata:', err);
        settle({
          success: false,
          errorMessage: err?.message || 'Ağ hatası',
        });
      }
    })();
  });
}

export default queryCustomerInfoViaRestP2P;
